import React, { useState, useEffect } from 'react';
import {
  Building2, Users, Award, Shield, FileEdit, Briefcase,
  Building2 as BuildingIcon, Upload, Info, X, DollarSign,
  FileText, ChevronDown, ChevronUp, Target
} from 'lucide-react';
import { CompanyInformation } from './CompanyInformation';
import { KeyPersonnel } from './KeyPersonnel';
import { PastPerformance } from './PastPerformance';
import { ComplianceDocuments } from './ComplianceDocuments';
import { ProposalTemplates } from './ProposalTemplates';
import { PreferredPrimes } from './PreferredPrimes';
import { Capabilities } from './Capabilities';
import { Documents } from './Documents';
import { ProfileAutoFill } from './ProfileAutoFill';
import { CostingProfile } from './CostingProfile';
import { ContractReadinessCard } from './ContractReadinessCard';
import { ComplianceExpirationWidget } from './ComplianceExpirationWidget';
import { useLocation, useNavigate } from 'react-router-dom';

interface ProfileNavigationProps {
  showMobileNav: boolean;
  onCloseMobileNav: () => void;
}

const sections = [
  { id: 'company', label: 'Company Information', icon: Building2, component: CompanyInformation },
  { id: 'capabilities', label: 'Capabilities', icon: Briefcase, component: Capabilities },
  { id: 'personnel', label: 'Key Personnel', icon: Users, component: KeyPersonnel },
  { id: 'performance', label: 'Past Performance', icon: Award, component: PastPerformance },
  { id: 'pricing', label: 'Pricing & Costing', icon: DollarSign, component: CostingProfile },
  { id: 'documents', label: 'Documents', icon: FileText, component: Documents },
  { id: 'compliance', label: 'Compliance', icon: Shield, component: ComplianceDocuments },
  { id: 'templates', label: 'Proposal Templates', icon: FileEdit, component: ProposalTemplates },
  { id: 'primes', label: 'Preferred Primes', icon: BuildingIcon, component: PreferredPrimes }
];

export function ProfileNavigation({ showMobileNav, onCloseMobileNav }: ProfileNavigationProps) {
  const [showAutoFill, setShowAutoFill] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentSection = location.hash.slice(1) || 'company';
  const ActiveComponent = sections.find(s => s.id === currentSection)?.component || sections[0].component;

  const handleSectionChange = (sectionId: string) => {
    navigate(`#${sectionId}`);
    onCloseMobileNav();
    setShowAutoFill(false);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Main content area */}
      <div className="flex-1 min-w-0 mx-auto max-w-3xl w-full">
        <div className="overflow-x-hidden">
          {showAutoFill ? <ProfileAutoFill onClose={() => setShowAutoFill(false)} /> : <ActiveComponent />}
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {showMobileNav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onCloseMobileNav} />
      )}

      {/* Mobile Navigation */}
      <div 
        className={`fixed inset-y-0 right-0 w-64 bg-bw-surface shadow-xl transform transition-transform duration-300 ease-in-out z-40 lg:hidden ${
          showMobileNav ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-medium text-bw-ink">Sections</h3>
            <button
              onClick={onCloseMobileNav}
              className="p-2 text-bw-muted hover:text-bw-ink rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = currentSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => handleSectionChange(section.id)}
                  className={`w-full flex items-center px-4 py-3 ${
                    isActive 
                      ? 'bg-bw-primary text-white' 
                      : 'text-bw-ink hover:bg-bw-primary-tint hover:text-bw-primary'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  <span className="text-sm font-medium">{section.label}</span>
                  {isActive && (
                    <span className="ml-auto">
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Auto-fill button */}
          <div className="p-4 border-t">
            <button
              onClick={() => {
                setShowAutoFill(true);
                onCloseMobileNav();
                navigate('#');
              }}
              className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors relative group ${
                showAutoFill
                  ? 'bg-bw-primary text-white'
                  : 'bg-bw-primary-tint text-bw-primary hover:bg-bw-primary-tint/80'
              }`}
            >
              <Upload className="h-5 w-5 mr-3" />
              <span>Auto-fill Profile</span>
              {!showAutoFill && (
                <div className="absolute left-full ml-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                  <div className="flex items-start">
                    <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                    <p>
                      Upload a capability statement or business profile document to automatically extract and fill your profile information. You'll be able to review and confirm all data before it's applied.
                    </p>
                  </div>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Navigation - hidden on mobile */}
      <div className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 space-y-4">
          {/* Navigation Card */}
          <div className="bg-bw-surface rounded-lg shadow">
            <nav className="space-y-1 p-4">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = !showAutoFill && currentSection === section.id;

                return (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors
                      ${isActive
                        ? 'bg-bw-primary text-white'
                        : 'text-bw-ink hover:bg-bw-surface-subtle'}`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    <span>{section.label}</span>
                    {isActive && (
                      <span className="ml-auto h-2 w-2 rounded-full bg-bw-surface"></span>
                    )}
                  </button>
                );
              })}

              {/* Auto-fill button */}
              <div className="pt-4 mt-4 border-t border-bw-border">
                <button
                  onClick={() => {
                    setShowAutoFill(true);
                    navigate('#');
                  }}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors relative group ${
                    showAutoFill
                      ? 'bg-bw-primary text-white'
                      : 'bg-bw-primary-tint text-bw-primary hover:bg-bw-primary-tint/80'
                  }`}
                >
                  <Upload className="h-5 w-5 mr-3" />
                  <span>Auto-fill Profile</span>
                  {!showAutoFill && (
                    <div className="absolute left-full ml-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                      <div className="flex items-start">
                        <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                        <p>
                          Upload a capability statement or business profile document to automatically extract and fill your profile information. You'll be able to review and confirm all data before it's applied.
                        </p>
                      </div>
                    </div>
                  )}
                </button>
              </div>
            </nav>
          </div>

          {/* Contract Readiness Widget */}
          <ContractReadinessCard />

          {/* Compliance Expiration Widget */}
          <ComplianceExpirationWidget compact />
        </div>
      </div>
    </div>
  );
}